import React, { useCallback, useRef, useState, useEffect } from 'react';
import { debounce } from 'underscore';
import { Input, Image, Button, mergeClasses } from '@fluentui/react-components';
import { Send20Regular } from '@fluentui/react-icons';
import PropTypes from 'prop-types';
import { AIPromptExamples } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { getHomePageGenerateWithAIComponentStyle } from '../home-page-styles';

const GenerateWithAIComponent = ({ i18n, onGenerateImage, loadingStatus }) => {
  const classes = getHomePageGenerateWithAIComponentStyle();

  const wrapperRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exampleList, setExampleList] = useState([]);

  const onInputChange = useCallback((event) => {
    setSearchQuery(event.target.value);

    if (event.key === 'Enter') {
      event.preventDefault();
      onGenerateImage({
        text: event.target.value.trim(),
      });
    }
  }, [onGenerateImage]);

  const handleSearch = useCallback(() => {
    onGenerateImage({
      text: searchQuery.trim(),
    });
  }, [onGenerateImage, searchQuery]);

  useEffect(() => {
    if (!loadingStatus) {
      const wrapperWidth = wrapperRef.current ? wrapperRef.current.offsetWidth : 0;
      const maxCount = wrapperWidth > 0 ? Math.floor(wrapperWidth / 264) : 1;
      const newList = AIPromptExamples.slice(0, maxCount);
      setExampleList(newList);
    } else {
      setExampleList([]);
    }
  }, [loadingStatus]);

  useEffect(() => {
    const updateExampleList = debounce((entries) => {
      if (entries[0]) {
        const { clientWidth } = entries[0].target;

        if (!loadingStatus) {
          const maxCount = clientWidth > 0 ? Math.floor(clientWidth / 264) : 1;
          const newList = AIPromptExamples.slice(0, maxCount);
          setExampleList(newList);
        }
      }
    }, 250);
    const resizeObserver = window.ResizeObserver && new ResizeObserver(updateExampleList);
    if (wrapperRef.current && resizeObserver) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      if (wrapperRef.current && resizeObserver) {
        resizeObserver.unobserve(wrapperRef.current);
      }
    };
  }, [loadingStatus]);

  return (
    <div className={classes.footerContainer} ref={wrapperRef}>
      <div className={classes.subHeading} role="heading" aria-level="2">
        {i18n.getString(_TL_('Generate images with AI'))}
      </div>
      <div className={classes.paragraph}>
        {i18n.getString(_TL_('Create images for your campaigns by providing prompts.'))}
      </div>
      <div className={classes.searchBoxWrapper}>
        <Input
          className={mergeClasses('generate-image-input-box', classes.searchBox)}
          placeholder={i18n.getString(_TL_('Describe the image you want to create'))}
          value={searchQuery}
          onChange={onInputChange}
          onKeyDown={onInputChange}
        />
        <Send20Regular className={classes.sendIcon} onClick={handleSearch} />
      </div>
      <div className={classes.imageRowContainer}>
        {exampleList.length > 0 && exampleList.map(image => (
          <div key={image.promptText} className={classes.imageWrapper}>
            <Image
              className={classes.image}
              src={image.imageSrc}
              alt={i18n.getString(image.promptText)}
            />
            <div className={`${classes.overlay} overlay`}>
              <div className={classes.overlayText}>
                {i18n.getString(image.promptText)}
              </div>
              <Button
                className={classes.overlayButton}
                onClick={() => onGenerateImage({ url: image.imageSrc, text: i18n.getString(image.promptText) })}
              >
                {i18n.getString(_TL_('Try it now'))}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

GenerateWithAIComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onGenerateImage: PropTypes.func.isRequired,
  loadingStatus: PropTypes.bool,
};

GenerateWithAIComponent.defaultProps = {
  loadingStatus: false,
};

export default GenerateWithAIComponent;
